import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { NavigationContextValue, SystemId, ViewId } from '../types/navigation';

const STORAGE_KEY_COLLAPSED = 'sidebar-collapsed';
const STORAGE_KEY_EXPANDED = 'sidebar-expanded-groups';
const DEFAULT_EXPANDED_GROUPS = ['ticketing'];

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_COLLAPSED) === 'true';
  } catch {
    return false;
  }
}

function getInitialExpandedGroups(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_EXPANDED);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch {
    // Fall through to default
  }
  return new Set(DEFAULT_EXPANDED_GROUPS);
}

function parseActiveNavFromPath(pathname: string): {
  activeSystemId: SystemId | null;
  activeViewId: ViewId | null;
} {
  const pathParts = pathname.split('/').filter(Boolean);
  const systemId = pathParts[0] as SystemId | undefined;
  const viewId = pathParts[1] as ViewId | undefined;

  const validSystems: SystemId[] = ['ticketing', 'knowledge-base', 'admin'];
  const isValidSystem = systemId && validSystems.includes(systemId);

  return {
    activeSystemId: isValidSystem ? systemId : null,
    activeViewId: isValidSystem && viewId ? viewId : null,
  };
}

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(getInitialCollapsed);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(getInitialExpandedGroups);
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Derive active system/view from URL
  const { activeSystemId, activeViewId } = useMemo(
    () => parseActiveNavFromPath(location.pathname),
    [location.pathname]
  );

  // Persist collapsed state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_COLLAPSED, String(isCollapsed));
    } catch {
      // Storage not available
    }
  }, [isCollapsed]);

  // Persist expanded groups
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify([...expandedGroups]));
    } catch {
      // Storage not available
    }
  }, [expandedGroups]);

  // Auto-expand active system group when navigating
  useEffect(() => {
    if (activeSystemId && !expandedGroups.has(activeSystemId)) {
      setExpandedGroups((prev) => new Set(prev).add(activeSystemId));
    }
  }, [activeSystemId, expandedGroups]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const expandGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      if (prev.has(groupId)) return prev;
      return new Set(prev).add(groupId);
    });
  }, []);

  const collapseGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      if (!prev.has(groupId)) return prev;
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  }, []);

  const contextValue = useMemo<NavigationContextValue>(
    () => ({
      isCollapsed,
      expandedGroups,
      activeSystemId,
      activeViewId,
      isMobileDrawerOpen,
      toggleCollapsed,
      setCollapsed,
      toggleGroup,
      expandGroup,
      collapseGroup,
      setMobileDrawerOpen,
    }),
    [
      isCollapsed,
      expandedGroups,
      activeSystemId,
      activeViewId,
      isMobileDrawerOpen,
      toggleCollapsed,
      setCollapsed,
      toggleGroup,
      expandGroup,
      collapseGroup,
    ]
  );

  return (
    <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>
  );
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return ctx;
}
